import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { DeployParamKind } from '@redviking/argonaut-util/src/mes/deployParam.zod';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { FlowNodeControl } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const sparkplugCommanderNodeSpec: NodeSpec<NodeCfg<'sparkplugCommander'>> = {
    requiredSockets: [ 'trigger' ],
    cfg: () => ({
        disabled: false,
        id: getUID(),
        kind: 'sparkplugCommander',
        width: 220,
        controlValues: {
            configDeploymentParameter: {
                deployParamId: '',
                deployParamKind: 'sparkplugNodeCfg' as DeployParamKind.sparkplugNodeCfg,
                type: 'deployParam',
            },
            connectionId: '',
            metricExpressions: [],
        },
    }),
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.MqttBroker ],
            },
            on: { 'update:value': defaultControlUpdateValueHandler },
            value: nodeCfg.controlValues.connectionId,
        }),
        // `configDeploymentParameter` has no dedicated control, so a `text` control is used to edit `deployParamId` directly
        configDeploymentParameter: defineControlOpts({
            cfg: { kind: 'text', label: 'Deployment Parameter' },
            value: nodeCfg.controlValues.configDeploymentParameter.deployParamId,
            on: {
                'update:value': (deployParamId, { flowEventEmitter }) => {
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                configDeploymentParameter: {
                                    ...nodeCfg.controlValues.configDeploymentParameter,
                                    deployParamId,
                                },
                            },
                        },
                    });
                },
            },
        }) as unknown as FlowNodeControl.WideOpts<NodeCfg<'sparkplugCommander'>['controlValues']['configDeploymentParameter']>,
        metricExpressions: defineControlOpts({
            cfg: {
                kind: 'button',
                label: 'Add Metric',
                useDialog: {
                    contentComponent: () => import('src/applet/design/flows/controls/button-control-dialogs/AddSparkplugMetricDialog.vue').then(m => m.default),
                },
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'click': (payload, { flowEventEmitter }) => {
                    const metric = payload as NodeCfg<'sparkplugCommander'>['controlValues']['metricExpressions'][number];
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                metricExpressions: [ ...nodeCfg.controlValues.metricExpressions, metric ],
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.metricExpressions,
        }),
    }),
    inputs: nodeCfg => {
        const inputs: ReturnType<NodeSpec<NodeCfg<'sparkplugCommander'>>['inputs']> = {
            trigger: { socket: 'event', label: 'Trigger' },
        };
        nodeCfg.controlValues.metricExpressions.forEach((metric, index) => {
            inputs[`input${index}`] = {
                socket: metric.dataType,
                controlOpts: defineControlOpts({
                    cfg: { kind: 'deletableSocket', label: metric.name },
                    on: {
                        'update:value': () => {}, // no-op, this control does not use the update:value event
                        'delete': (_, { flowEventEmitter }) => {
                            flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        metricExpressions: [
                                            ...nodeCfg.controlValues.metricExpressions.slice(0, index),
                                            ...nodeCfg.controlValues.metricExpressions.slice(index + 1),
                                        ],
                                    },
                                },
                                direction: 'input',
                                removalMethod: { index, prefix: 'input', kind: 'indexedIdentifier' },
                            });
                        },
                    },
                    enableWithConnectedSocket: true,
                    value: null,
                }),
            };
        });
        return inputs;
    },
    kindLabel: 'Sparkplug Commander',
    nodeCategory: 'connection',
    nodeSubCategory: 'Sparkplug',
    outputs: () => ({ event: { socket: 'event', label: 'Next' } }),
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.metricExpressions.length) {
            errors.metricExpressions = 'At least one metric expression is required';
        }
        if (!node.controlValues.configDeploymentParameter.deployParamId) {
            errors.configDeploymentParameter = 'Sparkplug Deployment Parameter is required';
        }
        return Object.keys(errors).length ? errors : true;
    },
};
